export type GuidePhotoAttribution = {
  displayName: string;
  uri?: string;
};

export type GuidePlaceAttribution = {
  provider: string;
  providerUri?: string;
};

export type DirectoryHostSource = "researched" | "manual" | "partner" | "google_places";

export type GuideDirectoryEntry = {
  id: string;
  slug?: string;
  placeId: string;
  category: string;
  name: string;
  summary?: string;
  about?: string;
  servicesOffered?: string[];
  serviceArea?: string[];
  city?: string;
  state?: string;
  formattedAddress?: string;
  description?: string;
  descriptionSource?: "editorial" | "generative";
  descriptionAttribution?: string;
  priceSummary?: string;
  websiteUrl?: string;
  bookingUrl?: string;
  phone?: string;
  mapsUrl?: string;
  photoUrl?: string;
  photoName?: string;
  photoAttribution?: string;
  photoAttributions?: GuidePhotoAttribution[];
  placeAttributions?: GuidePlaceAttribution[];
  lat?: number;
  lng?: number;
  source: DirectoryHostSource;
  reviewedAt?: string;
  ingestedAt?: string;
};

export type GuideDirectoryPayload = {
  version: number;
  region: string;
  ingestedAt: string | null;
  entries: GuideDirectoryEntry[];
};

export function buildGuideDirectoryPath(
  entry: GuideDirectoryEntry | string,
): string {
  if (typeof entry === "string") {
    return `/guides/${encodeURIComponent(entry)}`;
  }
  const segment = entry.slug || entry.id;
  return `/guides/${encodeURIComponent(segment)}`;
}

export function formatGuideDirectoryLocation(
  entry: GuideDirectoryEntry,
): string {
  if (entry.city && entry.state) {
    return `${entry.city}, ${entry.state}`;
  }
  if (entry.state) {
    return entry.state;
  }
  if (entry.formattedAddress) {
    return entry.formattedAddress;
  }
  return "United States";
}

export function formatGuideDirectoryServiceArea(
  entry: GuideDirectoryEntry,
): string {
  if (entry.serviceArea && entry.serviceArea.length > 0) {
    return entry.serviceArea.join(" · ");
  }
  return formatGuideDirectoryLocation(entry);
}

export function resolveGuideDirectoryPhotoUrl(
  entry: GuideDirectoryEntry,
  googlePhotoBuilder: (photoName: string, maxHeightPx: number) => string,
  maxHeightPx = 480,
): string | undefined {
  if (entry.photoUrl) {
    return entry.photoUrl;
  }
  if (entry.photoName) {
    return googlePhotoBuilder(entry.photoName, maxHeightPx);
  }
  return undefined;
}

export function resolveGuideDirectorySummary(entry: GuideDirectoryEntry): string | undefined {
  return entry.summary?.trim() || entry.description?.trim() || undefined;
}

export function resolveGuideDirectoryAbout(entry: GuideDirectoryEntry): string | undefined {
  return entry.about?.trim() || entry.description?.trim() || undefined;
}
